/**
 * useFriendships — Friendship data, listeners, and handlers.
 * Extracted from useMainApp for maintainability.
 */
import { useState, useEffect, useMemo } from "react";
import { db } from "../firebase";
import {
  collection, doc, setDoc, updateDoc, deleteDoc,
  onSnapshot, query, where, getDocs, serverTimestamp
} from "firebase/firestore";
import { logEvent, EVT } from "../analytics";
import { callFunction } from "../utils/callFunction";
import { friendshipId, buildFriendRequestDoc, buildFriendAcceptPatch } from "@sendly/shared";
import { useListenerRetry } from "./useListenerRetry";

export function useFriendships({ authUser }) {
  const [friendships, setFriendships] = useState([]);

  // Friendship listener (global, not gym-scoped)
  // Depend on authUser.uid (not authUser object) to avoid re-subscribing on reference changes
  const authUid = authUser?.uid;
  // An error callback through the shared retry: a listener without one dies
  // silently (and the SDK logs an "Uncaught Error"), e.g. when the session
  // ends under it on the profile page's sign-out grace period.
  const { nonce, onError, markAlive } = useListenerRetry("friendships", authUid || "");
  useEffect(() => {
    if (!authUid) {
      setFriendships([]);
      return;
    }
    const q = query(collection(db, "friendships"), where("users", "array-contains", authUid));
    const unsub = onSnapshot(q, snap => {
      markAlive();
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setFriendships(data);
    }, onError("friendships"));
    // Browsers throttle/suspend background connections (and iOS suspends the
    // PWA long-poll), so a change made in a parallel session (other browser /
    // PWA) can fail to stream in until this tab is refocused. Re-fetch on
    // visibility regain to reconcile. (Restores the behaviour from commit
    // 17cee03 that was lost when this hook was extracted from useMainApp.)
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        getDocs(q).then(snap => setFriendships(snap.docs.map(d => ({ id: d.id, ...d.data() })))).catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", handleVisibility);
    return () => { unsub(); document.removeEventListener("visibilitychange", handleVisibility); setFriendships([]); };
  }, [authUid, nonce, onError, markAlive]);

  // Derived friendship data
  const acceptedFriendUids = useMemo(() =>
    friendships.filter(f => f.status === "accepted").map(f => f.users.find(uid => uid !== authUid)),
    [friendships, authUid]
  );
  const pendingIncoming = useMemo(() =>
    friendships.filter(f => f.status === "pending" && f.requestedBy !== authUid),
    [friendships, authUid]
  );
  const pendingOutgoing = useMemo(() =>
    friendships.filter(f => f.status === "pending" && f.requestedBy === authUid),
    [friendships, authUid]
  );

  const handleSendFriendRequest = async (email) => {
    if (!authUser) return { error: "friends.userNotFound" };
    const trimmed = email.trim().toLowerCase();
    if (trimmed === authUser.email?.toLowerCase()) return { error: "friends.cannotAddSelf" };
    // User search goes through the server-side, rate-limited findUserByEmail
    // callable — the users collection is no longer client-queryable (a
    // client-readable user directory is enumerable). Returns only the matched
    // uid, never the email of anyone else, never a list.
    let targetUid;
    try {
      const res = await callFunction("findUserByEmail", { email: trimmed });
      if (!res.data?.found) return { error: "friends.userNotFound" };
      targetUid = res.data.uid;
    } catch (e) {
      if (e?.code === "functions/resource-exhausted") return { error: "friends.rateLimit" };
      return { error: "friends.userNotFound" };
    }
    const fid = friendshipId(authUser.uid, targetUid);
    const existing = friendships.find(f => f.id === fid);
    if (existing) {
      if (existing.status === "accepted") return { error: "friends.alreadyFriends" };
      return { error: "friends.requestPending" };
    }
    await setDoc(doc(db, "friendships", fid),
      buildFriendRequestDoc({ fromUid: authUser.uid, toUid: targetUid, requestedAt: serverTimestamp() }));
    logEvent(EVT.FRIEND_REQUEST_SENT);
    return { success: true };
  };

  const handleSendFriendRequestByUid = async (targetUid) => {
    if (!authUser || targetUid === authUser.uid) return;
    const fid = friendshipId(authUser.uid, targetUid);
    const existing = friendships.find(f => f.id === fid);
    if (existing) return;
    await setDoc(doc(db, "friendships", fid),
      buildFriendRequestDoc({ fromUid: authUser.uid, toUid: targetUid, requestedAt: serverTimestamp() }));
    logEvent(EVT.FRIEND_REQUEST_SENT);
  };

  const handleAcceptFriend = async (fid) => {
    await updateDoc(doc(db, "friendships", fid), buildFriendAcceptPatch({ acceptedAt: serverTimestamp() }));
    logEvent(EVT.FRIEND_REQUEST_ACCEPTED);
  };

  const handleDeleteFriendship = async (fid) => {
    // Optimistically drop it so the row disappears instantly even if the
    // realtime listener is throttled (backgrounded tab / PWA). The listener
    // reconciles afterwards; the delete rule always permits a participant, so
    // it can't flap back.
    setFriendships(prev => prev.filter(f => f.id !== fid));
    await deleteDoc(doc(db, "friendships", fid));
  };

  return {
    friendships, setFriendships,
    acceptedFriendUids, pendingIncoming, pendingOutgoing,
    handleSendFriendRequest, handleSendFriendRequestByUid,
    handleAcceptFriend, handleDeleteFriendship,
  };
}
